import React, { useMemo } from 'react';
import {
  Row,
  Col,
  Input,
  Button,
  Select,
  Switch,
  Typography,
  Divider,
  Space,
  TimePicker,
  Checkbox,
} from 'antd';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import useProfileStore, { getOfficeHoursErrors } from '../../../stores/useProfileStore';
import constants from '../../../constants';

const { Text } = Typography;

const OfficeHoursTab = ({ messageApi, glowEffect }) => {
  const {
    officeHoursLoading,
    acknowledgmentEnabled, acknowledgmentMsg, scheduleMode, availabilitySched, notCustomStartTime, notCustomEndTime,
    saveOfficeHours,
    setAcknowledgmentEnabled, setAcknowledgmentMsg, setScheduleMode,
    setAvailabilitySched, setNotCustomStartTime, setNotCustomEndTime
  } = useProfileStore();

  const errors = useMemo(
    () => getOfficeHoursErrors({ scheduleMode, availabilitySched, notCustomStartTime, notCustomEndTime }),
    [scheduleMode, availabilitySched, notCustomStartTime, notCustomEndTime]
  );

  const handleSaveOfficeHours = () => saveOfficeHours(messageApi);

  const handleToggleEnabled = (enabled) => {
    setAcknowledgmentEnabled(enabled);
    saveOfficeHours(messageApi);
  };

  return (
    <div style={{ padding: '0 40px', minHeight: '100%' }}>
      <div 
        style={{ 
          maxWidth: 800, 
          padding: '32px 0',
          transition: 'filter 0.5s ease',
          filter: glowEffect ? 'drop-shadow(0 0 10px rgba(76, 175, 80, 0.7))' : 'none'
        }}
      >
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 24px 0', color: '#263238' }}>Office Hours Settings</h3>
        
        <div style={{ padding: 24, border: '1px solid #f0f0f0', borderRadius: 8, backgroundColor: '#FAFAFA' }}>
          <Row gutter={24} style={{ marginBottom: 24 }}>
            <Col span={24}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                     <Text strong style={{ display: 'block', color: '#263238' }}>Enable Office Hours feature</Text>
                     <Text type="secondary" style={{ fontSize:13, fontWeight:400, display: 'block', maxWidth: 600, marginTop: 4 }}>
                       With Office Hours, we'll keep candidates engaged, even when you're asleep, or away for the weekend!
                     </Text>
                     <Text type="secondary" style={{ fontSize:13, fontWeight:400, display: 'block', maxWidth: 600, marginTop: 4 }}>
                       By setting your office hours and days below, when you receive a 1-1 text message "after hours", we'll send your customized "Out of Office" text message to your contact, so they know you may not respond right away.
                     </Text>
                  </div>
                  <Switch
                    checked={!!acknowledgmentEnabled}
                    loading={officeHoursLoading}
                    onChange={handleToggleEnabled}
                  />
               </div>
            </Col>
          </Row>

          {acknowledgmentEnabled && (
            <>
              <Divider style={{ margin: '12px 0 24px 0' }} />
              
              <Row gutter={24} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={12}>
                   <Text strong style={{ fontSize: 13, color: '#263238', display: 'block', marginBottom: 8 }}>
                     Schedule Mode
                   </Text>
                   <Select 
                     value={scheduleMode} 
                     onChange={setScheduleMode} 
                     style={{ width: '100%' }}
                   >
                     <Select.Option value="everyday">Every Day</Select.Option>
                     <Select.Option value="weekdays">Weekdays</Select.Option>
                     <Select.Option value="custom">Custom</Select.Option>
                   </Select>
                </Col>

                {scheduleMode !== 'custom' && (
                  <Col xs={24} lg={12}>
                     <Text strong style={{ fontSize: 13, color: '#263238', display: 'block', marginBottom: 8 }}>
                       Working Hours
                     </Text>
                     <Space>
                       <TimePicker
                         value={notCustomStartTime ? dayjs(notCustomStartTime.toDate()) : null}
                         onChange={(val) => setNotCustomStartTime(val ? moment(val.toDate()) : null)}
                         format="h:mm A"
                         use12Hours
                         status={errors.workingHoursError ? 'error' : undefined}
                         style={{ width: 110 }}
                         allowClear={false}
                       />
                       <Text type="secondary">-</Text>
                       <TimePicker
                         value={notCustomEndTime ? dayjs(notCustomEndTime.toDate()) : null}
                         onChange={(val) => setNotCustomEndTime(val ? moment(val.toDate()) : null)}
                         format="h:mm A"
                         use12Hours
                         status={errors.workingHoursError ? 'error' : undefined}
                         style={{ width: 110 }}
                         allowClear={false}
                       />
                     </Space>
                     {errors.workingHoursError && (
                       <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                         {errors.workingHoursError}
                       </Text>
                     )}
                  </Col>
                )}
              </Row>

              {scheduleMode === 'custom' && (
                <div style={{ marginBottom: 24, padding: '0 12px' }}>
                   {availabilitySched.map((day, index) => {
                     const dayError = errors.dayErrors[index];
                     return (
                     <Row key={day.key || index} align="middle" style={{ marginBottom: 12 }}>
                        <Col span={4}>
                           <Checkbox
                             checked={day.isActive}
                             onChange={(e) => {
                                const newSched = [...availabilitySched];
                                newSched[index] = { ...newSched[index], isActive: e.target.checked };
                                setAvailabilitySched(newSched);
                             }}
                           >
                              {day.name}
                           </Checkbox>
                        </Col>
                        <Col span={12}>
                           <Space>
                             <TimePicker
                               value={day.startTime ? dayjs(day.startTime.toDate()) : null}
                               onChange={(time) => {
                                  const newSched = [...availabilitySched];
                                  newSched[index] = { ...newSched[index], startTime: time ? moment(time.toDate()) : null };
                                  setAvailabilitySched(newSched);
                               }}
                               format="h:mm A"
                               use12Hours
                               disabled={!day.isActive}
                               status={dayError ? 'error' : undefined}
                               style={{ width: 110 }}
                               allowClear={false}
                             />
                             <Text type="secondary">-</Text>
                             <TimePicker
                               value={day.endTime ? dayjs(day.endTime.toDate()) : null}
                               onChange={(time) => {
                                  const newSched = [...availabilitySched];
                                  newSched[index] = { ...newSched[index], endTime: time ? moment(time.toDate()) : null };
                                  setAvailabilitySched(newSched);
                               }}
                               format="h:mm A"
                               use12Hours
                               disabled={!day.isActive}
                               status={dayError ? 'error' : undefined}
                               style={{ width: 110 }}
                               allowClear={false}
                             />
                           </Space>
                           {dayError && (
                             <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                               {dayError}
                             </Text>
                           )}
                        </Col>
                     </Row>
                     );
                   })}
                </div>
              )}

              <Row gutter={24}>
                 <Col span={24}>
                    <Text strong style={{ fontSize: 13, color: '#263238', display: 'block', marginBottom: 8 }}>
                      Out of office message
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13, fontWeight:400, display: 'block', marginBottom: 8 }}>
                      Send a message to the candidate when you are out of the office based on the schedule that you set above. 
                      This only applies to user 1-1 texting numbers.
                    </Text>
                    <Input.TextArea
                      value={acknowledgmentMsg}
                      onChange={(e) => setAcknowledgmentMsg(e.target.value)}
                      rows={4}
                      maxLength={constants.maxMessageLength}
                      showCount
                      placeholder="Enter your out of office message..."
                    />
                    
                 </Col>
              </Row>
            </>
          )}
        </div>

        {acknowledgmentEnabled && (
          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Button
              onClick={handleSaveOfficeHours}
              loading={officeHoursLoading}
              type="primary"
              style={{ backgroundColor: '#4CAF50', borderColor: '#4CAF50' }}
            >
              Save Office Hours
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficeHoursTab;
